import Container from 'typedi'
import Vue from 'vue'
import ErrorHandlerService from '../../services/error-handler.service'
import Notification from '../../services/notification.service'
import { mapState } from 'vuex'
import MultiFactorMixin from '@/modules/common/components/mixins/multifactor.mixin'

type Option = {
  text: string;
  value: string;
}

export default Vue.extend({
  name: 'GtrEventCorrelator',

  mixins: [MultiFactorMixin],

  computed: {
    ...mapState('event', ['events']),
    ...mapState('security', ['user']),

    userHasAccessToAllEvents (): boolean {
      return (this as any).user?.access_to_all_future_events && this.availableCompanyEventsWithoutAccess.length === 0
    },

    userHasAccessToNoEvents (): boolean {
      return !(this as any).user?.access_to_all_future_events && this.eventsWithAccess.length === 0
    },

    disableRevokeAccessButton (): boolean {
      return (this as any).loading || (this as any).eventsToRevokeAccessFrom.length < 1
    },

    disableGrantAccessButton (): boolean {
      return (this as any).loading || (this as any).eventsToGrantAccessTo.length < 1
    },

    uuidsOfEventsWithAccess (): string[] {
      return (this as any).user.admin_events.filter(({ event }: any) => event !== null && event.status === 'ACTIVE').map(({ event: { uuid } }: any) => uuid)
    },

    userCompany (): Record<string, any> | null {
      return (this as any).user.company ? (this as any).events.find((company: any) => (company.uuid === (this as any).user.company.uuid)) : null
    },

    availableCompanyEventsWithoutAccess (): Option[] {
      return this.userCompany?.events?.filter(({ uuid }: any) => !this.uuidsOfEventsWithAccess.includes(uuid)).map(({ name: text, uuid: value }: any) => ({ text, value })) ?? []
    },

    noUserCompany (): boolean {
      return (this as any).user.company === null
    },

    eventsWithAccess (): Option[] {
      return (this as any).user.admin_events.filter(({ event }: any) => event !== null && event.status === 'ACTIVE').map(({ event }: Record<string, any>) => ({ text: event.name, value: event.uuid })) ?? []
    }
  },

  data () {
    return {
      show2FAConfirmModal: false,
      two_factor_token: null,
      valid_two_factor_token: false,
      // Component state variables
      allEventsAccess: null as boolean | null,
      loading: false,
      deleting: false,
      submitting: false,
      eventsToGrantAccessTo: [] as string[],
      eventsToRevokeAccessFrom: [] as Array<Record<string, any>>,
      headers: [
        { text: 'Accessible Events', align: 'start', sortable: false, value: 'text' }
      ]
    }
  },

  async mounted () {
    // after first mounted, these will handle the state when the
    this.$bus.$on('close-event-access', this.resetCorrelator)
  },

  methods: {
    handleClose2FAConfirmModal () {
      this.$data.show2FAConfirmModal = false
    },

    async handleAllEventsAccess (action?: string) {
      try {
        this.submitting = true
        const responseStatus = await (this as any).sendTwoFactorCode('UPDATE_USER')
        if (responseStatus === 201 || responseStatus === 204) {
          if (action === 'grant') {
            this.allEventsAccess = true
          } else if (action === 'revoke') {
            this.allEventsAccess = false
          }
        }
        if (responseStatus === 204) {
          this.valid_two_factor_token = true
          this.two_factor_token = null
          this.$data.show2FAConfirmModal = false
          await this.setFutureAccess()
        } else {
          this.valid_two_factor_token = false
          this.$data.show2FAConfirmModal = true
        }
      } catch (error) {
        Container.get(ErrorHandlerService).error(error)
      } finally {
        this.submitting = false
      }
    },

    async verifyTokenSave () {
      const result = await (this as any).verifyToken()
      if (!result) return
      this.$data.show2FAConfirmModal = false
      setTimeout(this.setFutureAccess, 1500)
      this.valid_two_factor_token = false
    },

    async setFutureAccess () {
      try {
        this.loading = true
        if (this.allEventsAccess === false) {
          this.eventsToRevokeAccessFrom = this.eventsWithAccess
          await this.revokeEventsAccess()
        }
        await this.$store.dispatch('security/updateUser', { user_uuid: (this as any).user.uuid, data: { access_to_all_future_events: this.allEventsAccess } })
        Container.get(Notification).success('Access to all events successfully ' + (this.allEventsAccess ? 'granted' : 'revoked'))
        this.allEventsAccess = null
      } catch (error) {
        Container.get(ErrorHandlerService).error(error)
      } finally {
        this.loading = false
      }
    },

    async grantEventsAccess () {
      await this.$store.dispatch('security/addEventAccessBulk', {
        user_uuid: (this as any).user.uuid,
        add_events: this.eventsToGrantAccessTo
      })
      this.eventsToGrantAccessTo = []
    },

    async revokeEventsAccess () {
      await this.$store.dispatch('security/removeEventAccessBulk', {
        user_uuid: (this as any).user.uuid,
        remove_events: this.eventsToRevokeAccessFrom.map((event: any) => event.value)
      })
      this.eventsToRevokeAccessFrom = []
    },

    async handleGrantEventsAccess () {
      if (!this.eventsToGrantAccessTo.length) {
        Container.get(ErrorHandlerService).error('Please select some events to grant access to.')
        return
      }

      try {
        this.loading = true
        await this.grantEventsAccess()
        Container.get(Notification).success('Access to selected events successfully granted')
        await this.$store.dispatch('security/getUser', { user_uuid: (this as any).user.uuid })
      } catch (error) {
        Container.get(ErrorHandlerService).error(error)
      } finally {
        this.loading = false
      }
    },

    async handleRevokeEventsAccess () {
      if (!this.eventsToRevokeAccessFrom.length) {
        Container.get(ErrorHandlerService).error('Please select some events to revoke access from.')
        return
      }

      try {
        this.loading = true
        this.deleting = true
        await this.revokeEventsAccess()
        Container.get(Notification).success('Access to selected events successfully revoked')
        await this.$store.dispatch('security/getUser', { user_uuid: (this as any).user.uuid })
      } catch (error) {
        Container.get(ErrorHandlerService).error(error)
      } finally {
        this.loading = false
        this.deleting = false
      }
    },

    /**
     * utility function that will reset state.
     */
    resetCorrelator (): void {
      this.loading = false
      this.deleting = false
      this.submitting = false
      this.allEventsAccess = null
      this.eventsToGrantAccessTo = []
      this.eventsToRevokeAccessFrom = []
    }
  }
})
